/**
 * Module visibility definitions.
 * Used by Layout.js for nav filtering and UsersTab.js for module assignment.
 * 
 * Each module maps to a nav section key in Layout.js.
 * Role defaults define what each role sees out of the box.
 * Per-user overrides can add or remove modules from their defaults.
 */

// All available modules with their sub-items
export const MODULE_DEFINITIONS = [
  { key: 'admin', label: 'Admin & System', description: 'System settings, docs, DB health', minRole: 6,
    items: [
      { path: '/organization', label: 'System Admin' },
      { path: '/system-docs', label: 'System Docs' },
      { path: '/health-dashboard', label: 'DB Health', minRole: 7 },
    ]
  },
  { key: 'portfolio', label: 'Portfolio', description: 'Clients, facilities, floor plans, portfolio map', minRole: 3,
    items: [
      { path: '/clients', label: 'Clients' },
      { path: '/facilities', label: 'Facilities' },
      { path: '/floor-plans', label: 'Floor Plans' },
      { path: '/portfolio-map', label: 'Portfolio Map' },
    ]
  },
  { key: 'planning', label: 'Planning Hub', description: 'AI Dashboard, capital planning, reports', minRole: 6, featureGate: 'fca_enabled',
    items: [
      { path: '/ai-dashboard', label: 'AI Dashboard' },
      { path: '/finance-hub?tab=executive', label: 'Executive Summary' },
      { path: '/finance-hub?tab=capital', label: 'Capital Planning' },
      { path: '/finance-hub?tab=maintenance', label: 'Maintenance Costs' },
      { path: '/finance-hub?tab=scorecard', label: 'Scorecard' },
      { path: '/finance-hub?tab=scenarios', label: 'Replacement Scenarios' },
      { path: '/finance-hub?tab=export', label: 'Export Reports' },
    ]
  },
  { key: 'assessments', label: 'Assessment Hub', description: 'FCA assessments, inspections, regional analysis', minRole: 3, featureGate: 'fca_enabled',
    items: [
      { path: '/admin', label: 'Assessment Admin', minRole: 6 },
      { path: '/review-queue', label: 'Review Queue', minRole: 4 },
      { path: '/assessments', label: 'Assessments' },
      { path: '/inspection-planner', label: 'Inspection Planner' },
      { path: '/regional-analysis', label: 'Regional Analysis' },
    ]
  },
  { key: 'work_management', label: 'Work Management', description: 'Work orders, assets, PM, scheduling, dispatch', minRole: 2, featureGate: 'iwms_enabled',
    items: [
      { path: '/iwms-admin', label: 'WMS Admin', minRole: 6 },
      { path: '/work-orders', label: 'Work Orders' },
      { path: '/messaging', label: 'Messaging' },
      { path: '/iwms-assets', label: 'Assets', minRole: 3 },
      { path: '/pm-schedules', label: 'PM Schedules', minRole: 3 },
      { path: '/warranties', label: 'Warranties', minRole: 3 },
      { path: '/leases', label: 'Leases', minRole: 3 },
      { path: '/vendors', label: 'Vendors', minRole: 5 },
      { path: '/finance-hub?tab=vendor', label: 'Vendor Performance', minRole: 5 },
      { path: '/scheduler', label: 'Scheduler', minRole: 5 },
      { path: '/live-map', label: 'Live Map', minRole: 5 },
      { path: '/work-order-map', label: 'Work Order Map', minRole: 5 },
      { path: '/gis-map', label: 'GIS Dashboard', minRole: 5 },
    ]
  },
  { key: 'technician', label: 'My Schedule', description: 'Technician work assignments and schedule', minRole: 2, featureGate: 'iwms_enabled',
    items: [
      { path: '/technician', label: 'My Schedule' },
    ]
  },
];

// Role hierarchy levels (must match backend)
const ROLE_LEVELS = {
  requester: 1, technician: 2, field_surveyor: 3, inspector: 3, surveyor: 3,
  sme: 4, planner: 5, facility_manager: 5, admin: 6, client_admin: 6,
  super_admin: 7, founder: 8,
};

// Default modules per role (what each role sees out of the box)
export const ROLE_DEFAULTS = {
  requester:      ['work_management'],
  technician:     ['work_management', 'technician'],
  field_surveyor: ['portfolio', 'assessments'],
  inspector:      ['portfolio', 'assessments'],
  surveyor:       ['portfolio', 'assessments'],
  sme:            ['portfolio', 'assessments'],
  planner:        ['portfolio', 'assessments', 'work_management'],
  facility_manager: ['portfolio', 'work_management'],
  admin:          ['admin', 'portfolio', 'planning', 'assessments', 'work_management'],
  client_admin:   ['portfolio', 'planning', 'assessments', 'work_management'],
  super_admin:    ['admin', 'portfolio', 'planning', 'assessments', 'work_management', 'technician'],
  founder:        ['admin', 'portfolio', 'planning', 'assessments', 'work_management', 'technician'],
};

/**
 * Get the effective visible modules for a user.
 * Priority: user override > role defaults
 * @param {object} user - User object with role and optional visible_modules
 * @param {object} features - Feature flags (fca_enabled, iwms_enabled)
 * @returns {string[]} Array of visible module keys
 */
export function getVisibleModules(user, features = {}) {
  if (!user?.role) return MODULE_DEFINITIONS.map(m => m.key);
  
  const roleLevel = ROLE_LEVELS[user.role] || 1;
  
  let modules;
  if (user.visible_modules && user.visible_modules.length > 0) {
    modules = [...user.visible_modules];
  } else {
    modules = [...(ROLE_DEFAULTS[user.role] || ROLE_DEFAULTS.requester)];
  }
  
  return modules.filter(modKey => {
    const modDef = MODULE_DEFINITIONS.find(m => m.key === modKey);
    if (!modDef) return false;
    if (roleLevel < modDef.minRole) return false;
    if (modDef.featureGate && features[modDef.featureGate] === false) return false;
    return true;
  });
}

/**
 * Check if a specific nav item (by path) is visible for the user.
 */
export function isItemVisible(user, itemPath) {
  if (!user) return true;
  const hiddenItems = user.hidden_items || [];
  if (hiddenItems.includes(itemPath)) return false;
  return true;
}

/**
 * Get modules available for a role (for the admin toggle UI).
 */
export function getAvailableModulesForRole(role, features = {}) {
  const roleLevel = ROLE_LEVELS[role] || 1;
  return MODULE_DEFINITIONS.filter(m => {
    if (roleLevel < m.minRole) return false;
    if (m.featureGate && features[m.featureGate] === false) return false;
    return true;
  });
}
